import { CodeOutlined, UserOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { CarrierResponseModel } from "api/repositories/carrier/model/CarrierResponseModel";
import { ActionsComponentType } from "components/generalComponents/actionsComponent/model/ActionsComponentModel";
import EclipseComponent from "components/generalComponents/eclipseComponent/EclipseComponent";
import { FilterAttributes } from "components/generalComponents/filterComponents/model/FilterComponentsModel";
import { AirTimeSupplierModel } from "features/airTimeManagement/data/model/detail/AirtimeManagementDetailResponseModel";
import { AirtimeDetailEntity } from "features/airTimeManagement/domain/entities/AirtimeDetailEntity";
import { AirtimeResponseEntity } from "features/airTimeManagement/domain/entities/AirtimeResponseEntity";
import { isEmpty } from "lodash";
import moment from "moment";
import { formatCurrency, getUserStatus } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export enum TempCarrierId {
  reddi = "465ff323-f627-4dac-8646-6c6798c31cb7",
  vnmobile = "5bf938d7-db4a-4bb1-8cc7-0ff7630936dc",
  vinaphone = "d1a4a6c7-b923-4480-b6f2-222a44c52d23",
  mobifone = "7ff6f64c-b8a4-48f2-93c7-b67d74bd30a8",
  viettel = "92a5f0a4-a69f-4c4f-a14b-12e32f86fafd",
}

export class AirTimeManagementConstants {
  static mainColumns = (localStrings: typeof strings) => {
    return Object?.entries(localStrings.AirTimeManagement.Columns)
      ?.filter(([keys]) => keys !== "updatedAt")
      ?.map(([key, val]) => {
        return {
          title: val,
          dataIndex: key,
          align: "center",
          width:
            key === "type" || key === "status"
              ? "9%"
              : key === "amount"
                ? "11%"
                : "auto",
          sorter: key === "createdAt" ? true : false,
          ellipsis: {
            showTitle: true,
          },
          key: `airtime_management_${key}`,
          render: (text) =>
            key === "status" ? (
              getUserStatus(text)
            ) : key === "createdAt" ? (
              moment(text).format("DD/MM/YYYY HH:mm:ss")
            ) : key === "type" ? (
              localStrings.AirTimeManagement.Type[text]
            ) : key === "amount" ? (
              formatCurrency(text, false, false)
            ) : key === "url" ? (
              !!text ? (
                <EclipseComponent data={{ value: text }} />
              ) : (
                localStrings.GlobalLabels.NoInfo
              )
            ) : key === "order" ? (
              text
            ) : !!text ? (
              text
            ) : (
              localStrings.GlobalLabels.NoInfo
            ),
        };
      }) as ColumnsType<AirtimeResponseEntity>;
  };

  static filters = (
    localStrings: typeof strings,
    carrierList: Array<CarrierResponseModel>,
  ) => {
    return Object?.entries(localStrings.AirTimeManagement.Filters)?.map(
      ([key, val]) => {
        return {
          colLg: 4,
          defaultValue:
            key === "type" || key === "status"
              ? localStrings.GlobalLabels.All
              : "",
          filterName: key,
          prefixIcon:
            key === "name" ? (
              <UserOutlined className="pr-1" />
            ) : key === "code" ? (
              <CodeOutlined className="pr-1" />
            ) : (
              <></>
            ),
          filterType:
            key === "type" || key === "status" || key === "carrier"
              ? "select"
              : key === "amount"
                ? "price-number"
                : "input",
          labelName: val,
          needCurrency: key === "amount",
          placeholder: `${val}...`,
          options:
            key === "type"
              ? [
                  {
                    label: localStrings.GlobalLabels.All,
                    value: -1,
                  },
                  {
                    label: localStrings.AirTimeManagement.Type[0],
                    value: 0,
                  },
                ]
              : key === "status"
                ? [
                    {
                      label: localStrings.GlobalLabels.All,
                      value: -1,
                    },
                    {
                      label: localStrings.GlobalLabels.Active,
                      value: 0,
                    },
                    {
                      label: localStrings.GlobalLabels.Inactive,
                      value: 1,
                    },
                  ]
                : key === "carrier"
                  ? [
                      { label: localStrings.GlobalLabels.All, value: "" },
                      ...carrierList?.map((child) => ({
                        label: child?.name,
                        value: child?.id,
                      })),
                    ]
                  : [],
        };
      },
    ) as Array<FilterAttributes>;
  };

  static actions(
    carrierList: Array<CarrierResponseModel>,
    detailInfo: AirtimeDetailEntity,
    localStrings: typeof strings,
  ) {
    return Object?.entries(localStrings.AirTimeManagement.Detail)
      ?.filter(([keys]) => keys !== "order")
      ?.map(([key, val]) => {
        return {
          colLg: key === "description" || key === "config" ? 12 : 8,
          label: val,
          name: key,
          type:
            key === "status" || key === "type" || key === "carrier"
              ? "select"
              : key === "description" || key === "config"
                ? "text-area"
                : key === "amount"
                  ? "input-number"
                  : "input",
          pointerEvents:
            key === "name" ||
            key === "type" ||
            key === "status" ||
            key === "description" ||
            key === "amount" ||
            key === "config" ||
            key === "carrier"
              ? false
              : true,
          createFormRules:
            key !== "amount"
              ? {
                  stricted:
                    key === "name" ||
                    key === "code" ||
                    key === "status" ||
                    key === "type" ||
                    key === "amount" ||
                    key === "carrier"
                      ? true
                      : false,
                  type:
                    key === "type" || key === "status" ? "number" : "string",
                  message: Object?.entries(
                    strings.AirTimeManagement.Message,
                  )?.find(([keys, _]) => keys === key)?.[1],
                }
              : undefined,
          needCurrency: key === "amount",
          rules:
            key === "amount"
              ? [
                  {
                    required: true,
                    message: localStrings.GlobalMessage.Currency,
                  },
                ]
              : undefined,
          options:
            key === "status"
              ? [
                  {
                    label: localStrings.GlobalLabels.Active,
                    value: 0,
                  },
                  {
                    label: localStrings.GlobalLabels.Inactive,
                    value: 1,
                  },
                ]
              : key === "type"
                ? [
                    {
                      label: localStrings.AirTimeManagement.Type[0],
                      value: 0,
                    },
                  ]
                : key === "carrier"
                  ? carrierList?.map((child) => ({
                      label: child?.name,
                      value: child?.id,
                    }))
                  : [],
          placeholder: Object?.entries(strings.AirTimeManagement.Message)?.find(
            ([keys, _]) => keys === key,
          )?.[1],
          detailKey: key as string,
          numberRange: key === "amount" ? [1000] : undefined,
          initialVals:
            key === "amount" && isEmpty(detailInfo) ? 1000 : detailInfo[key],
        };
      }) as ActionsComponentType<AirtimeDetailEntity>[];
  }

  static detailSupplier() {
    return Object.entries(strings.AirTimeManagement.Supplier).map(
      ([key, val]) => {
        return {
          colLg: 8,
          label: val,
          name: key,
          type: key === "description" ? "text-area" : "input",
          pointerEvents: true,
          placeholder: `${val}...`,
          detailKey: key,
        };
      },
    ) as ActionsComponentType<AirTimeSupplierModel>[];
  }
}
